import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LongPage, TocEntry } from "./SideNav";
import {
  Bullet,
  Bullets,
  DefItem,
  DefList,
  Ext,
  Mail,
  Note,
  PageHeader,
  Pull,
  Section,
  SimpleTable,
  SubHeading,
} from "./ui";
import { CONTACT_EMAIL, LINKS } from "./constants";

/** The page's texts, always in English. */
const useEnglishT = () => {
  const { i18n } = useTranslation();
  return { t: i18n.getFixedT("en", "securityPage") };
};

/** The four layers a request passes through, as a plain vertical figure. */
const TopologyFigure: React.FC = () => {
  const { t } = useEnglishT();
  const layers: Array<{ label: string; detail: string; accent?: boolean }> = [
    { label: t("topology.label1"), detail: t("topology.detail1") },
    { label: t("topology.label2"), detail: t("topology.detail2") },
    { label: t("topology.label3"), detail: t("topology.detail3") },
    { label: t("topology.label4"), detail: t("topology.detail4"), accent: true },
  ];

  return (
    <figure className="my-7 rounded-2xl border border-sand-200 bg-white p-5 sm:p-6">
      <ol className="space-y-3">
        {layers.map((layer, i) => (
          <li key={layer.label}>
            <div
              className={`rounded-xl border p-4 ${
                layer.accent
                  ? "border-accent/25 bg-accent-pale/50"
                  : "border-sand-200 bg-sand-50"
              }`}
            >
              <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-600">
                {layer.label}
              </p>
              <p className="mt-2 text-[0.9375rem] leading-relaxed text-[#3B4A48]">{layer.detail}</p>
            </div>
            {i < layers.length - 1 && (
              <div className="flex justify-center py-1 text-sand-300" aria-hidden="true">
                <svg width="14" height="20" viewBox="0 0 14 20" fill="none">
                  <path d="M7 0v14" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M2 13l5 6 5-6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                </svg>
              </div>
            )}
          </li>
        ))}
      </ol>
      <figcaption className="mt-5 border-t border-sand-200 pt-4 text-sm leading-relaxed text-ink-600">
        {t("topology.figcaption")}
      </figcaption>
    </figure>
  );
};

/**
 * Worked example for §02: one subject whose kennitala is written two ways —
 * bare in its own column, and inside a clinical note with the "kt." cue —
 * ending up under one pseudonym. The code shown is abbreviated; the real
 * output is a full 44-character base64 string.
 */
const RedactionExample: React.FC = () => {
  const { t } = useEnglishT();
  const CODE = "Vv9xK2mN7pQ…iJkLmNo=";
  const rows: Array<{ label: string; id: React.ReactNode; note: React.ReactNode }> = [
    {
      label: t("redaction.rowLabel1"),
      id: "1234567890",
      note: "Kom í eftirlit 3. mars. Sjá eldri nótu, kt. 123456-7890.",
    },
    {
      label: t("redaction.rowLabel2"),
      id: CODE,
      note: `Kom í eftirlit 3. mars. Sjá eldri nótu, kt. ${CODE}.`,
    },
  ];

  return (
    <figure className="my-7 overflow-hidden rounded-xl border border-sand-200 bg-white">
      {rows.map((row, i) => (
        <div key={row.label} className={i > 0 ? "border-t border-sand-200 bg-accent-pale/30" : ""}>
          <p className="px-5 pt-4 text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-600">
            {row.label}
          </p>
          <dl className="grid gap-x-6 gap-y-1 px-5 pb-4 pt-3 sm:grid-cols-[7rem,1fr]">
            <dt className="text-sm font-medium text-ink-600">kennitala</dt>
            <dd className="break-all font-mono text-[0.8125rem] text-ink">{row.id}</dd>
            <dt className="mt-2 text-sm font-medium text-ink-600 sm:mt-1">athugasemd</dt>
            <dd className="break-words font-mono text-[0.8125rem] leading-relaxed text-ink sm:mt-1">
              {row.note}
            </dd>
          </dl>
        </div>
      ))}
      <figcaption className="border-t border-sand-200 bg-sand-50 px-5 py-3 text-sm leading-relaxed text-ink-600">
        {t("redaction.figcaption")}
      </figcaption>
    </figure>
  );
};

export const SecurityTechnicalPage: React.FC = () => {
  const { t } = useEnglishT();
  const { t: tLocal, i18n } = useTranslation("securitySummary");

  const TOC: TocEntry[] = [
    { id: "approach", label: t("toc.approach") },
    { id: "pseudonymization", label: t("toc.pseudonymization") },
    { id: "storage", label: t("toc.storage") },
    { id: "accounts", label: t("toc.accounts") },
    { id: "access-control", label: t("toc.accessControl") },
    { id: "detection", label: t("toc.detection") },
    { id: "audit-trail", label: t("toc.auditTrail") },
    { id: "operations", label: t("toc.operations") },
    { id: "review", label: t("toc.review") },
  ];

  return (
    <div lang="en">
    <LongPage
      toc={TOC}
      tocHeading="On this page"
      intro={
        <>
          {i18n.language !== "en" && (
            <p lang={i18n.language} className="mb-6 max-w-3xl rounded-lg border border-sand-200 bg-sand-50 px-4 py-3 text-sm text-ink-600">
              {tLocal("englishOnlyNotice")}{" "}
              <Link to="/security" className="text-accent underline underline-offset-2">
                {tLocal("englishOnlyBackLink")}
              </Link>
            </p>
          )}
          <PageHeader
            kicker={t("header.kicker")}
            title={t("header.title")}
            lead={<p>{t("header.lead")}</p>}
          />
        </>
      }
    >
      <div className="divide-y divide-sand-200 [&>section:not(:first-child)]:pt-14 [&>section]:pb-14 [&>section:last-child]:pb-0">
        <Section id="approach" number="01" title={t("approach.title")}>
          <p>{t("approach.p1")}</p>
          <p>
            {t("approach.p2a")}
            <strong>{t("approach.p2Strong1")}</strong>
            {t("approach.p2Mid")}
            <strong>{t("approach.p2Strong2")}</strong>
            {t("approach.p2b")}
          </p>

          <SubHeading>{t("approach.whoHeading")}</SubHeading>
          <SimpleTable
            head={[t("approach.tableHead0"), t("approach.tableHead1")]}
            rows={[
              [t("approach.row1Adversary"), t("approach.row1Defense")],
              [t("approach.row2Adversary"), t("approach.row2Defense")],
              [t("approach.row3Adversary"), t("approach.row3Defense")],
              [t("approach.row4Adversary"), t("approach.row4Defense")],
              [t("approach.row5Adversary"), t("approach.row5Defense")],
              [t("approach.row6Adversary"), t("approach.row6Defense")],
              [t("approach.row7Adversary"), t("approach.row7Defense")],
            ]}
          />
        </Section>

        <Section id="pseudonymization" number="02" title={t("pseudonymization.title")}>
          <p>
            {t("pseudonymization.p1a")}
            <strong>{t("pseudonymization.p1Strong")}</strong>
            {t("pseudonymization.p1b")}
          </p>
          <p>
            {t("pseudonymization.p2a")}
            <em>{t("pseudonymization.p2Em1")}</em>
            {t("pseudonymization.p2Mid")}
            <em>{t("pseudonymization.p2Em2")}</em>
            {t("pseudonymization.p2b")}
          </p>

          <Bullets>
            <Bullet>
              {t("pseudonymization.bullet1a")}
              <Ext href={LINKS.vault}>HashiCorp Vault</Ext>
              {t("pseudonymization.bullet1b")}
            </Bullet>
            <Bullet>
              {t("pseudonymization.bullet2a")}
              <em>{t("pseudonymization.bullet2Em1")}</em>
              {t("pseudonymization.bullet2Mid")}
              <em>{t("pseudonymization.bullet2Em2")}</em>
              {t("pseudonymization.bullet2b")}
            </Bullet>
            <Bullet>{t("pseudonymization.bullet3")}</Bullet>
            <Bullet>
              {t("pseudonymization.bullet4a")}
              <strong>{t("pseudonymization.bullet4Strong")}</strong>
              {t("pseudonymization.bullet4b")}
            </Bullet>
          </Bullets>

          <SubHeading>{t("pseudonymization.sameCodeHeading")}</SubHeading>
          <p>
            {t("pseudonymization.canonA")}
            <code>123456-7890</code>
            {t("pseudonymization.canonB")}
            <code>1234567890</code>
            {t("pseudonymization.canonC")}
          </p>
          <p>
            {t("pseudonymization.mattersA")}
            <em>{t("pseudonymization.mattersEm")}</em>
            {t("pseudonymization.mattersB")}
          </p>

          <RedactionExample />

          <p>{t("pseudonymization.p3")}</p>

          <Note tone="caution" title={t("pseudonymization.rotationTitle")}>
            <p>{t("pseudonymization.rotationBody")}</p>
          </Note>
        </Section>

        <Section id="storage" number="03" title={t("storage.title")}>
          <p>
            {t("storage.p1a")}
            <Ext href={LINKS.aesGcm}>AES-256-GCM</Ext>
            {t("storage.p1b")}
          </p>

          <DefList>
            <DefItem term={t("storage.mappingsTerm")}>{t("storage.mappingsBody")}</DefItem>
            <DefItem term={t("storage.filesTerm")}>
              {t("storage.filesA")}
              <strong>{t("storage.filesStrong")}</strong>
              {t("storage.filesB")}
            </DefItem>
            <DefItem term={t("storage.secondFactorTerm")}>{t("storage.secondFactorBody")}</DefItem>
            <DefItem term={t("storage.catalogueTerm")}>
              <strong>{t("storage.catalogueStrong")}</strong>
              {t("storage.catalogueRest")}
            </DefItem>
          </DefList>

          <SubHeading>{t("storage.retentionHeading")}</SubHeading>
          <p>
            {t("storage.retP1a")}
            <em>{t("storage.retP1Em")}</em>
            {t("storage.retP1b")}
          </p>
          <p>
            {t("storage.destroyA")}
            <strong>{t("storage.destroyStrong")}</strong>
            {t("storage.destroyB")}
          </p>
          <Bullets>
            <Bullet>
              <strong>{t("storage.survivesStrong")}</strong>
              {t("storage.survivesRest")}
            </Bullet>
            <Bullet>
              <strong>{t("storage.graceStrong")}</strong>
              {t("storage.graceRest")}
            </Bullet>
            <Bullet>
              <strong>{t("storage.partialStrong")}</strong>
              {t("storage.partialRest")}
            </Bullet>
          </Bullets>
          <p>{t("storage.sweepOutro")}</p>
          <Note tone="neutral" title={t("storage.retentionNoteTitle")}>
            <p>
              {t("storage.retainA")}
              <strong>{t("storage.retainStrong")}</strong>
              {t("storage.retainB")}
            </p>
          </Note>
        </Section>

        <Section id="accounts" number="04" title={t("accounts.title")}>
          <SubHeading>{t("accounts.gettingHeading")}</SubHeading>
          <Bullets>
            <Bullet>
              <strong>{t("accounts.regStrong")}</strong>
              {t("accounts.regRest")}
            </Bullet>
            <Bullet>
              {t("accounts.invA")}
              <strong>{t("accounts.invStrong")}</strong>
              {t("accounts.invB")}
            </Bullet>
          </Bullets>

          <SubHeading>{t("accounts.secondFactorHeading")}</SubHeading>
          <p>
            {t("accounts.twoFaA")}
            <Ext href={LINKS.totpRfc}>{t("accounts.twoFaLink")}</Ext>
            {t("accounts.twoFaB")}
          </p>
          <p>
            <strong>{t("accounts.codesStrong")}</strong>
            {t("accounts.codesRest")}
          </p>

          <SubHeading>{t("accounts.sessionsHeading")}</SubHeading>
          <Bullets>
            <Bullet>
              {t("accounts.sessA")}
              <strong>{t("accounts.sessStrong")}</strong>
              {t("accounts.sessB")}
            </Bullet>
            <Bullet>
              {t("accounts.lookupA")}
              <strong>{t("accounts.lookupStrong")}</strong>
              {t("accounts.lookupB")}
            </Bullet>
            <Bullet>
              {t("accounts.refreshA")}
              <strong>{t("accounts.refreshStrong")}</strong>
              {t("accounts.refreshB")}
            </Bullet>
            <Bullet>
              {t("accounts.expA")}
              <strong>{t("accounts.expStrong")}</strong>
              {t("accounts.expB")}
            </Bullet>
          </Bullets>

          <SubHeading>{t("accounts.passwordsHeading")}</SubHeading>
          <p>
            {t("accounts.pwA")}
            <Ext href={LINKS.pwnedPasswords}>{t("accounts.pwLink")}</Ext>
            {t("accounts.pwMid")}
            <em>{t("accounts.pwEm")}</em>
            {t("accounts.pwB")}
          </p>

          <SubHeading>{t("accounts.rateLimitHeading")}</SubHeading>
          <p>
            {t("accounts.rlA")}
            <code>Retry-After</code>
            {t("accounts.rlB")}
          </p>
          <SimpleTable
            head={[t("accounts.tableHead0"), t("accounts.tableHead1")]}
            rows={[
              [t("accounts.row1What"), t("accounts.row1Limit")],
              [t("accounts.row2What"), t("accounts.row2Limit")],
              [t("accounts.row3What"), t("accounts.row3Limit")],
              [t("accounts.row4What"), t("accounts.row4Limit")],
              [t("accounts.row5What"), t("accounts.row5Limit")],
              [t("accounts.row6What"), t("accounts.row6Limit")],
              [t("accounts.row7What"), t("accounts.row7Limit")],
            ]}
          />
          <p>
            {t("accounts.failedA")}
            <strong>{t("accounts.failedStrong")}</strong>
            {t("accounts.failedB")}
          </p>
          <Note tone="neutral">
            <p>{t("accounts.budgetNote")}</p>
          </Note>
        </Section>

        <Section id="access-control" number="05" title={t("accessControl.title")}>
          <p>{t("accessControl.p1")}</p>

          <Bullets>
            <Bullet>
              <strong>{t("accessControl.cfStrong")}</strong>
              {t("accessControl.cfRest")}
            </Bullet>
            <Bullet>
              <strong>{t("accessControl.twoScopesStrong")}</strong>
              {t("accessControl.twoScopesA")}
              <em>{t("accessControl.twoScopesEm1")}</em>
              {t("accessControl.twoScopesMid")}
              <em>{t("accessControl.twoScopesEm2")}</em>
              {t("accessControl.twoScopesB")}
            </Bullet>
            <Bullet>
              <strong>{t("accessControl.defStrong")}</strong>
              {t("accessControl.defRest")}
            </Bullet>
            <Bullet>
              <strong>{t("accessControl.thinStrong")}</strong>
              {t("accessControl.thinRest")}
            </Bullet>
            <Bullet>
              <strong>{t("accessControl.matrixStrong")}</strong>
              {t("accessControl.matrixRest")}
            </Bullet>
          </Bullets>

          <SubHeading>{t("accessControl.rolesHeading")}</SubHeading>
          <p>{t("accessControl.rolesP1")}</p>
          <DefList>
            <DefItem term={t("accessControl.visTerm")}>{t("accessControl.visBody")}</DefItem>
            <DefItem term={t("accessControl.escTerm")}>{t("accessControl.escBody")}</DefItem>
            <DefItem term={t("accessControl.cliTerm")}>{t("accessControl.cliBody")}</DefItem>
            <DefItem term={t("accessControl.providersTerm")}>
              {t("accessControl.providersBody")}
            </DefItem>
          </DefList>
        </Section>

        <Section id="detection" number="06" title={t("detection.title")}>
          <p>{t("detection.p1")}</p>
          <Bullets>
            <Bullet>
              <strong>{t("detection.colStrong")}</strong>
              {t("detection.colRest")}
            </Bullet>
            <Bullet>
              <strong>{t("detection.freeStrong")}</strong>
              {t("detection.freeRest")}
            </Bullet>
          </Bullets>
          <p>
            {t("detection.upA")}
            <strong>{t("detection.upStrong")}</strong>
            {t("detection.upB")}
          </p>
          <Note tone="caution" title={t("detection.noteTitle")}>
            <p>{t("detection.noteBody")}</p>
          </Note>
        </Section>

        <Section id="audit-trail" number="07" title={t("auditTrail.title")}>
          <p>{t("auditTrail.p1")}</p>

          <Pull>{t("auditTrail.pull")}</Pull>

          <p>{t("auditTrail.p2")}</p>
          <p>
            {t("auditTrail.p3a")}
            <em>{t("auditTrail.p3Em")}</em>
            {t("auditTrail.p3b")}
          </p>
          <p>{t("auditTrail.p4")}</p>
        </Section>

        <Section id="operations" number="08" title={t("operations.title")}>
          <TopologyFigure />

          <SubHeading>{t("operations.edgeHeading")}</SubHeading>
          <p>{t("operations.edgeP1")}</p>
          <p>
            {t("operations.cspA")}
            <strong>{t("operations.cspStrong1")}</strong>
            {t("operations.cspMid1")}
            <strong>{t("operations.cspStrong2")}</strong>
            {t("operations.cspB")}
          </p>

          <SubHeading>{t("operations.addressHeading")}</SubHeading>
          <p>{t("operations.addressP1")}</p>

          <SubHeading>{t("operations.configHeading")}</SubHeading>
          <Bullets>
            <Bullet>{t("operations.secretsBullet")}</Bullet>
            <Bullet>
              <strong>{t("operations.ucStrong")}</strong>
              {t("operations.ucRest")}
            </Bullet>
            <Bullet>{t("operations.uploadBoundBullet")}</Bullet>
            <Bullet>{t("operations.richTextBullet")}</Bullet>
          </Bullets>

          <SubHeading>{t("operations.supplyHeading")}</SubHeading>
          <p>
            {t("operations.scA")}
            <strong>{t("operations.scStrong")}</strong>
            {t("operations.scB")}
          </p>
          <p>{t("operations.pushIntro")}</p>
          <SimpleTable
            head={[t("operations.tableHead0"), t("operations.tableHead1")]}
            rows={[
              [t("operations.row1Check"), t("operations.row1Catches")],
              [t("operations.row2Check"), t("operations.row2Catches")],
              [t("operations.row3Check"), t("operations.row3Catches")],
              [t("operations.row4Check"), t("operations.row4Catches")],
              [t("operations.row5Check"), t("operations.row5Catches")],
            ]}
          />
          <p>{t("operations.testsP")}</p>
          <p>{t("operations.hostP")}</p>
        </Section>

        <Section id="review" number="09" title={t("review.title")}>
          <p>
            {t("review.gdprA")}
            <Ext href={LINKS.gdpr}>GDPR</Ext>
            {t("review.gdprMid")}
            <Ext href={LINKS.personuvernd}>Persónuvernd</Ext>
            {t("review.gdprB")}
          </p>
          <p>{t("review.gapsIntro")}</p>

          <DefList>
            <DefItem term={t("review.opTerm")}>
              {t("review.opA")}
              <strong>{t("review.opStrong")}</strong>
              {t("review.opB")}
            </DefItem>
            <DefItem term={t("review.rotationTerm")}>{t("review.rotationBody")}</DefItem>
            <DefItem term={t("review.recoveryTerm")}>{t("review.recoveryBody")}</DefItem>
            <DefItem term={t("review.auditTerm")}>{t("review.auditBody")}</DefItem>
            <DefItem term={t("review.keyStoreTerm")}>{t("review.keyStoreBody")}</DefItem>
            <DefItem term={t("review.mailTerm")}>{t("review.mailBody")}</DefItem>
            <DefItem term={t("review.breachTerm")}>{t("review.breachBody")}</DefItem>
            <DefItem term={t("review.rateLimitTerm")}>{t("review.rateLimitBody")}</DefItem>
            <DefItem term={t("review.detectionTerm")}>{t("review.detectionBody")}</DefItem>
          </DefList>

          <p>
            {t("review.roadmapA")}
            <Link to="/roadmap" className="text-accent underline underline-offset-2">
              {t("review.roadmapLink")}
            </Link>
            {t("review.roadmapB")}
          </p>

          <SubHeading>{t("review.vulnHeading")}</SubHeading>
          <p>
            {t("review.vulnA")}
            <Mail address={CONTACT_EMAIL} subject="Security report" />
            {t("review.vulnB")}
          </p>
        </Section>
      </div>
    </LongPage>
    </div>
  );
};
